import { FC, FocusEvent, memo, startTransition, useEffect, useRef } from 'react';
import { Control, useForm } from 'react-hook-form';
import type { GroupBase, SelectInstance } from 'react-select';

import { MaterieelTypenSearchField } from '@/components/UI/Fields/ReactFormFields/MaterieelTypenSearchField';
import { useMaterieelTypenSearch } from '@/hooks/useMaterieelTypenSearch';
import { LabeledEntryOrString } from '@/types/LabeledEntry';

import usePlankTableStore from '../../hooks/usePlankTableStore';
import { columnVoorraadDisplayName, ddpColumns } from '../../utils/ddp';
import CheckButton from '../CheckButton/CheckButton';

import IndelenButton from './IndelenButton';
import IndelenNavigatie from './IndelenNavigatie';
import RbcKnoopSelectField from './RbcKnoopSelectField';

import plankViewStyles from '../../PlankView.module.css';
import indelenStyles from './Indelen.module.css';

type IndelenPanelProps = {
  open: boolean | undefined;
  setOpen: (val?: boolean) => void;
};
type IndelenPanelType = {
  materieelTypen: LabeledEntryOrString[] | null;
};
const MaterieelField = memo(MaterieelTypenSearchField);
const IndelenPanel: FC<IndelenPanelProps> = ({ open, setOpen }) => {
  const tableView = usePlankTableStore((state) => state.tableView);
  const availableMatTypes = usePlankTableStore((state) => state.availableMatTypes);
  const toggleCollapse = usePlankTableStore((state) =>
    state.tableView === 'materieel' ? state.toggleCollapse : state.toggleCollapseRegionaal,
  );
  const toggleColumn = usePlankTableStore((state) => state.toggleColumn);
  const showColumn = usePlankTableStore((state) => state.showColumn);
  const rows = usePlankTableStore((state) => state.getRows());
  const collapsed = usePlankTableStore((state) => state.getCollapsed());

  const filterKnopen = usePlankTableStore((state) => state.tableFilters.knopen);
  const setFilterKnopen = usePlankTableStore((state) => state.setTableFilterKnopen);
  const persistedRows = usePlankTableStore((state) => state.table.rows);
  const { selectedMaterieelTypen, handleMaterieelTypenChange, createOption } = useMaterieelTypenSearch(persistedRows);

  useEffect(() => {
    if (tableView === 'materieel') {
      startTransition(() => {
        availableMatTypes(selectedMaterieelTypen);
      });
    }
  }, [tableView, selectedMaterieelTypen]);

  const { control } = useForm<IndelenPanelType>({
    mode: 'onSubmit',
  });

  const inputRef = useRef<SelectInstance<unknown, boolean, GroupBase<unknown>> | null>(null);
  const knoopRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && inputRef.current && tableView === 'materieel') inputRef.current.focus();
    if (open && knoopRef.current && tableView === 'knoop') knoopRef.current.focus();
  }, [tableView, open]);

  return (
    <div
      className={indelenStyles.indelenPanelStyle}
      onBlur={(event: FocusEvent<HTMLDivElement, Element>) => {
        const relatedTarget = event.relatedTarget as HTMLElement;
        if (
          relatedTarget &&
          relatedTarget.tagName === 'BUTTON' &&
          relatedTarget.textContent?.trim() === 'Begin met zoeken'
        ) {
          // Niet open event trigger
        } else if (!event.currentTarget.contains(relatedTarget) || relatedTarget?.id === 'noOnBlur') {
          setOpen(false);
        }
      }}
    >
      <div className={indelenStyles.indelenButtonsStyle}>
        <IndelenButton tabIndex={0} action={setOpen} />
        <IndelenButton
          tabIndex={0}
          asAutomatischIndelen
          action={toggleCollapse}
          collapsed={collapsed.length === rows.length}
          id='noOnBlur'
        />
      </div>

      <div className={`${indelenStyles.indelenPanelContent} ${open ? indelenStyles.open : ''}`}>
        <div style={{ display: open ? 'flex' : 'none', flexDirection: 'column', gap: '8px' }}>
          <h1 className={plankViewStyles.plankH1}>Filteren</h1>
          <h3 className={plankViewStyles.plankH3}>Rijen</h3>
          <h5 className={plankViewStyles.plankH5}>Rijen</h5>
          <IndelenNavigatie
            afterNavigation={(tableView) => {
              if (tableView === 'materieel' && inputRef?.current) inputRef.current.focus();
              if (tableView === 'knoop' && knoopRef?.current) knoopRef.current.focus();
            }}
          />
          {tableView === 'materieel' && (
            <MaterieelField
              ref={inputRef}
              id='materieelTypen'
              name='materieelTypen'
              data-testId='materieelTypen'
              control={control as Control<any>}
              defaultValue={selectedMaterieelTypen.map((item) => createOption(item))}
              onChangeCallback={handleMaterieelTypenChange}
            />
          )}
          {tableView === 'knoop' && (
            <>
              <h5 className={plankViewStyles.plankH5}>Locatie</h5>
              <div className={plankViewStyles.plankFlex}>
                <RbcKnoopSelectField ref={knoopRef} value={filterKnopen} onSelect={setFilterKnopen} />
              </div>
            </>
          )}
          <h3 className={plankViewStyles.plankH3}>Kolommen</h3>
          <h5 className={plankViewStyles.plankH5}>Kolom</h5>
          <div className={plankViewStyles.plankFlex}>
            {ddpColumns.map((col) => (
              <div key={`button_${col}`}>
                <CheckButton checked={showColumn(col)} onClick={() => toggleColumn(col)}>
                  {col === 'Voorraadbeheer' ? columnVoorraadDisplayName() : col}
                </CheckButton>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
export default IndelenPanel;
